import { useMemo, useState } from "react";
import { Link } from "react-router";
import arrowLeftIcon from "../assets/figma-documents/arrow-left.svg";
import arrowRightIcon from "../assets/figma-documents/arrow-right.svg";
import chevronDownIcon from "../assets/figma-documents/chevron-down.svg";
import searchIcon from "../assets/figma-documents/search.svg";
import { DocumentRow, DocumentStatus } from "../types";

export interface DocumentsTableProps {
  documents: DocumentRow[];
  filter: DocumentStatus | "all";
  onFilterChange: (filter: DocumentStatus | "all") => void;
  search: string;
  onSearchChange: (search: string) => void;
  loading?: boolean;
}

const TABS: Array<{ value: DocumentStatus | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "fix_requested", label: "Fix required" },
  { value: "rejected", label: "Rejected" },
  { value: "signed", label: "Approved" },
];

export function DocumentsTable({
  documents,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  loading,
}: DocumentsTableProps) {
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(documents.length / pageSize));
  const visiblePage = Math.min(currentPage, totalPages);
  const paginatedDocs = useMemo(() => {
    const start = (visiblePage - 1) * pageSize;
    return documents.slice(start, start + pageSize);
  }, [documents, visiblePage, pageSize]);

  return (
    <div className="documents-table flex flex-col gap-3 font-sans">
      {/* Controls Bar: Tabs on left, Search on right (Figma 35:1991 / 37:7286) */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        {/* Figma Tabs Container (padding 8px, gap 8px, bg #F7F8F8, rounded-2xl) */}
        <div className="flex items-center gap-2 p-2 bg-[#F7F8F8] rounded-2xl w-full sm:w-fit overflow-x-auto" role="tablist" aria-label="Document status">
          {TABS.map((tab) => {
            const isActive = filter === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`min-w-20 px-4 py-3 rounded-2xl text-base whitespace-nowrap transition-all cursor-pointer ${
                  isActive
                    ? "bg-[#FDFFFC] text-[#0B2981] font-bold shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)]"
                    : "text-[#000000] font-semibold hover:text-[#012699] hover:bg-white/60"
                }`}
                onClick={() => {
                  onFilterChange(tab.value);
                  setCurrentPage(1);
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Figma Search Input Wrap (border 1.5px #DFE1E2, rounded-full, w-311px) */}
        <div className="relative w-full sm:w-[311px]">
          <img src={searchIcon} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" alt="" aria-hidden="true" />
          <input
            type="text"
            className="w-full pl-11 pr-4 py-3 text-base bg-[#FDFFFC] border-[1.5px] border-[#DFE1E2] rounded-full placeholder:text-[#A9AEB1] text-[#011627] focus:outline-none focus:border-[#012699] focus:ring-2 focus:ring-[#012699]/15 transition-all shadow-2xs"
            value={search}
            onChange={(e) => {
              onSearchChange(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search with codes, or terms"
            aria-label="Search documents"
          />
        </div>
      </div>

      {/* Main Table Card (Figma 35:1991 / 37:7286) */}
      <div className="bg-[#FDFFFC] rounded-2xl shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] overflow-hidden p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#F2F4FA] text-base font-bold text-[#011627]">
                <th className="py-4.5 px-6 font-bold">Document</th>
                <th className="py-4.5 px-6 font-bold">Status</th>
                <th className="py-4.5 px-6 font-bold">Sent by</th>
                <th className="py-4.5 px-6 font-bold">Acting doctor</th>
                <th className="py-4.5 px-6 font-bold">Updated</th>
                <th className="py-4.5 px-6 font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6E8EA] text-sm">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-[#7F8A92] font-medium text-base">
                    Loading documents...
                  </td>
                </tr>
              ) : paginatedDocs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-[#7F8A92] font-medium text-base">
                    No documents match the selected filter.
                  </td>
                </tr>
              ) : (
                paginatedDocs.map((doc) => (
                  <tr key={doc.id} className="hover:bg-[#F2F4FA]/40 transition-colors">
                    <td data-label="Document" className="py-4.5 px-6">
                      <div className="font-bold text-[#011627] text-base leading-snug">
                        {doc.patient_ref || "Untitled"}
                      </div>
                      <div className="text-sm text-[#7F8A92] mt-0.5 truncate max-w-xs font-normal">
                        {doc.extracted_data?.documentClassification || doc.file_name}
                      </div>
                    </td>
                    <td data-label="Status" className="py-4.5 px-6 whitespace-nowrap">
                      <FigmaStatusChip
                        status={doc.status}
                        revisionPending={doc.status === "pending" && (!!doc.fix_requested_at || !!doc.previous_document_id)}
                      />
                    </td>
                    <td data-label="Sent by" className="py-4.5 px-6">
                      <div className="font-bold text-[#011627] text-base leading-tight">
                        {doc.nurse_name || doc.nurse_email || "Unknown nurse"}
                      </div>
                      {doc.nurse_email && (
                        <div className="text-sm text-[#7F8A92] mt-0.5 truncate max-w-[200px] font-normal">
                          {doc.nurse_email}
                        </div>
                      )}
                    </td>
                    <td data-label="Acting doctor" className="py-4.5 px-6 whitespace-nowrap">
                      <span className="font-medium text-[#24313D] text-base">
                        {doc.doctor_name || (doc.doctor_npi ? `NPI: ${doc.doctor_npi}` : "—")}
                      </span>
                    </td>
                    <td data-label="Updated" className="py-4.5 px-6 whitespace-nowrap">
                      <span className="text-sm text-[#7F8A92]">
                        {formatDate(doc.signed_at || doc.fix_requested_at || doc.rejected_at || doc.created_at)}
                      </span>
                    </td>
                    <td data-label="Action" className="py-4.5 px-6 text-right whitespace-nowrap">
                      <Link
                        to={`/documents/${doc.id}`}
                        className="inline-flex items-center justify-center px-5 py-2 rounded-full text-base font-bold text-white bg-[#012699] hover:bg-[#0B2981] transition-all"
                      >
                        Review
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 py-4 bg-[#FDFFFC] text-sm text-[#7F8A92]">
          <div className="flex items-center gap-2">
            <span className="font-normal text-[#7F8A92]">Rows per page</span>
            <div className="relative">
              <select
                className="appearance-none pl-3 pr-7 py-1.5 bg-[#FDFFFC] border border-[#E6E8EA] rounded-lg text-sm font-bold text-[#011627] focus:outline-none focus:border-[#012699] cursor-pointer"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <img src={chevronDownIcon} className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none" alt="" aria-hidden="true" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="font-normal text-[#7F8A92]">
              Page {visiblePage} of <strong className="text-[#011627] font-bold">{totalPages}</strong>
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E6E8EA] bg-white text-[#7F8A92] hover:text-[#011627] hover:bg-[#F2F4FA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                disabled={visiblePage <= 1}
                onClick={() => setCurrentPage(visiblePage - 1)}
                aria-label="Previous page"
              >
                <img src={arrowLeftIcon} alt="" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E6E8EA] bg-white text-[#7F8A92] hover:text-[#011627] hover:bg-[#F2F4FA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                disabled={visiblePage >= totalPages}
                onClick={() => setCurrentPage(visiblePage + 1)}
                aria-label="Next page"
              >
                <img src={arrowRightIcon} alt="" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
    </div>
  );
}

export function FigmaStatusChip({
  status,
  revisionPending = false,
  review = false,
  className = "",
}: {
  status: DocumentStatus;
  revisionPending?: boolean;
  review?: boolean;
  className?: string;
}) {
  const baseClasses =
    "inline-flex items-center justify-center whitespace-nowrap shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold leading-none select-none";

  if (revisionPending) {
    return (
      <span className={`${baseClasses} bg-[#F2F4FA] text-[#0B2981] ${className}`}>
        Revision pending
      </span>
    );
  }

  switch (status) {
    case "signed":
      return (
        <span className={`${baseClasses} bg-[#E3F5E1] text-[#008817] ${className}`}>
          Approved
        </span>
      );
    case "fix_requested":
      return (
        <span className={`${baseClasses} bg-[#F9EEEE] text-[#D83933] ${className}`}>
          Fix required
        </span>
      );
    case "rejected":
      return (
        <span className={`${baseClasses} bg-[#F9EEEE] text-[#D83933] ${className}`}>
          Rejected
        </span>
      );
    case "pending":
    default:
      return (
        <span
          className={`${baseClasses} ${
            review ? "bg-[#F2F4FA] text-[#012699]" : "bg-[#F2F4FA] text-[#012699]"
          } ${className}`}
        >
          {review ? "Pending review" : "Pending"}
        </span>
      );
  }
}

function formatDate(val: string | null): string {
  if (!val) return "—";
  try {
    return new Date(val).toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return val;
  }
}
